/**
 * canonicalBalances — the single source of truth for one user's USDT and shares.
 *
 * Pure function (no I/O, no DB) so it is trivially unit-testable and can be
 * shared by every surface: the My Shares dashboard (api/user/my-shares.js),
 * the dashboard wallet (api/user/dashboard-data.js), the client Statement
 * (lib/accountLedger.ts) and the admin Master Audit.
 *
 * Locked rules (see .augment/plans/plan-financial-reconciliation-2026-07-01.md):
 *  - Q1: EQUITY (owned) shares = active non-topup purchases (which already
 *    include ALL commission/pool/ACRES shares as COMMISSION_LEVEL_* /
 *    'Performance Pool' / 'ACRES_REDEMPTION' rows) + approved conversions
 *    + legacy_shares + net completed transfers. commission_transactions
 *    .share_commission and multi_level_commissions.shares are the audit trail
 *    of shares ALREADY materialised as active purchases and are therefore NOT
 *    added again (they are reported as an informational wallet.shares figure).
 *  - Q2: WALLET USDT (spendable) is ANCHORED to commission_balances.usdt_balance
 *    (this gates real withdrawals). The gap versus the raw-ledger walk
 *    (earned + topup − withdrawn − converted) is preserved as `residual`
 *    (fees / rounding / manual corrections) — never silently overwritten.
 *  - Q3: conversions that spent USDT but credited 0 shares still SUBTRACT the
 *    usdt_amount from the walk and raise a CONVERSION_ZERO_SHARES flag.
 */

/** @typedef {{shares_purchased?:any,bonus_shares?:any,status?:any,payment_method?:any,is_commission_topup?:any,topup_payment_amount?:any,total_amount?:any,purchaser_type?:any}} PurchaseRow */
/** @typedef {{usdt_commission?:any,share_commission?:any,status?:any}} CtRow */
/** @typedef {{usdt_commission?:any,shares_purchased?:any,status?:any}} MlcRow */
/** @typedef {{shares_requested?:any,usdt_amount?:any,status?:any}} ConversionRow */
/** @typedef {{amount?:any,net_amount?:any,status?:any}} WithdrawalRow */
/** @typedef {{withdrawal_amount?:any,status?:any}} WithdrawalRequestRow */
/** @typedef {{shares_transferred?:any,status?:any}} TransferRow */
/** @typedef {{usdt_balance?:any,share_balance?:any,total_earned_usdt?:any,total_earned_shares?:any,total_withdrawn?:any,escrowed_amount?:any,escrowed_shares?:any,legacy_shares?:any}} CommissionBalanceRow */
/**
 * @typedef {Object} CanonicalRawData
 * @property {PurchaseRow[]} [purchases]
 * @property {CtRow[]} [commissionTransactions]
 * @property {MlcRow[]} [multiLevelCommissions]
 * @property {ConversionRow[]} [conversions]
 * @property {WithdrawalRow[]} [withdrawalsExecuted]
 * @property {WithdrawalRequestRow[]} [withdrawalRequests]
 * @property {TransferRow[]} [transfersSent]
 * @property {TransferRow[]} [transfersReceived]
 * @property {CommissionBalanceRow|null} [commissionBalance]
 */

export const SHARE_TOL = 0.001
export const USDT_TOL = 0.01

/**
 * v4.8.250: payment_methods whose shares are FREE/BONUS shares that were written into
 * `shares_purchased` (with total_amount 0) rather than into `bonus_shares`.
 *
 * Bonus is stored three ways across the ledger's history:
 *   (a) pure gift rows      — bonus_shares > 0, shares_purchased = 0  (handled by giftBonusShares)
 *   (b) bonus-as-shares     — shares_purchased > 0, total_amount = 0  (this list)
 *   (c) reversal/correction — shares_purchased < 0, total_amount = 0  (also this list, so they net off)
 *
 * Identical list to the admin Master Audit bonus column (supabase/migrations/
 * 20260916_bonus_column_net_correct.sql, v4.8.246) so the user dashboard and the admin
 * view can never disagree about what counts as a bonus share.
 *
 * DISPLAY ONLY. These shares are already inside activePurchaseShares and therefore already
 * inside equityShares — this list is used to RECLASSIFY them on the breakdown, never to add
 * them to the total again.
 */
export const BONUS_AS_SHARES_METHODS = new Set([
  'Consolidated Bonus',
  'Legacy Bonus',
  'Glitch Correction',
  'SINGLE_SPECIAL_2026_BUYER_GIFT',
  'SINGLE_SPECIAL_2026_UPLINE_GIFT',
  'SINGLE_SPECIAL_2026_CORRECTION',
  'BIRTHDAY_2026_BUYER_GIFT',
  'BIRTHDAY_2026_UPLINE_GIFT',
  'BIRTHDAY_2026_PRESALE_GIFT',
  'PCAP_2026_BUYER_GIFT',
  'PCAP_2026_UPLINE_GIFT',
])

/** True when a purchase row's shares are free/bonus shares filed under shares_purchased. */
export function isBonusAsSharesMethod(method) {
  return BONUS_AS_SHARES_METHODS.has(String(method || '').trim())
}

const norm = (s) => String(s ?? '').trim().toLowerCase()
const num = (v) => (typeof v === 'number' ? v : parseFloat(v ?? 0)) || 0
export const round = (n, d = 2) => +(Number(n) || 0).toFixed(d)

const S_PURCHASE_ACTIVE = new Set(['active'])
const S_TOPUP = new Set(['approved', 'active', 'completed', 'paid'])
const S_COMMISSION = new Set(['approved', 'paid', 'completed'])
const S_CONVERSION = new Set(['approved'])
const S_WITHDRAWAL = new Set(['completed', 'paid'])
const S_TRANSFER = new Set(['completed'])

const sum = (rows, fn) => (rows || []).reduce((s, r) => s + fn(r), 0)
const pm = (r) => String(r?.payment_method || '')
// COMPANY-attributed purchases belong to a company the user directs, NOT the
// director personally. They are excluded from this PERSONAL canonical balance
// and reported separately (see api/user/company get_company_holdings). Legacy /
// pre-attribution rows carry no purchaser_type and are treated as PERSONAL.
const isCompanyPurchase = (r) => norm(r?.purchaser_type) === 'company'

/**
 * Compute one user's canonical balances from raw ledger rows.
 * @param {CanonicalRawData} raw
 */
export function computeCanonicalBalances(raw) {
  // Drop COMPANY-attributed rows up front so NO personal derivation below
  // (equity, gift bonus, commission-level, topup wallet credit, flags) can
  // ever count a company's shares against the director personally. This is the
  // single exclusion point; total-supply aggregates live elsewhere and are
  // intentionally NOT filtered here (all real shares still count toward supply).
  const purchases = (raw?.purchases || []).filter((p) => !isCompanyPurchase(p))
  const cts = raw?.commissionTransactions || []
  const mlcs = raw?.multiLevelCommissions || []
  const conversions = raw?.conversions || []
  const wExec = raw?.withdrawalsExecuted || []
  const wReq = raw?.withdrawalRequests || []
  const tSent = raw?.transfersSent || []
  const tRecv = raw?.transfersReceived || []
  const bal = raw?.commissionBalance || {}

  // ── Equity (owned) shares — active non-topup purchases carry every credited share ──
  const activePurchases = purchases.filter(
    (p) => !p.is_commission_topup && S_PURCHASE_ACTIVE.has(norm(p.status)),
  )
  const activePurchaseShares = sum(activePurchases, (p) => num(p.shares_purchased))
  // Gift bonus (birthday / presale): stored as bonus_shares on a row whose
  // shares_purchased is 0 (see lib/birthdayGiftIssuance.js). These are real owned
  // shares that were NOT in the headline. Fold them in ONLY where shares_purchased
  // is 0 — purchase-tier bonuses already sit inside shares_purchased (approve-payment
  // / verify-bsc write shares_purchased = base + bonus AND echo bonus_shares), so
  // adding those would double-count. The sp==0 guard cleanly separates the two.
  const giftBonusShares = sum(
    activePurchases.filter((p) => num(p.shares_purchased) === 0),
    (p) => num(p.bonus_shares),
  )
  // v4.8.250 DISPLAY ONLY: free/bonus shares that were written into shares_purchased
  // (BOGO, Birthday/Anniversary, PCAP buyer + upline gifts, Consolidated/Legacy Bonus)
  // plus their negative reversal rows, which net off automatically. Already counted in
  // activePurchaseShares, so this is NEVER added to equityShares — it only moves these
  // shares off the "paid" line and onto the "bonus" line in the breakdown.
  const bonusAsShares = sum(
    activePurchases.filter((p) => isBonusAsSharesMethod(pm(p))),
    (p) => num(p.shares_purchased),
  )
  const commissionLevelShares = sum(
    activePurchases.filter((p) => pm(p).toUpperCase().startsWith('COMMISSION_LEVEL')),
    (p) => num(p.shares_purchased),
  )
  const performancePoolShares = sum(
    activePurchases.filter((p) => pm(p) === 'Performance Pool'),
    (p) => num(p.shares_purchased),
  )
  const acresRedemptionShares = sum(
    activePurchases.filter((p) => pm(p).toUpperCase() === 'ACRES_REDEMPTION'),
    (p) => num(p.shares_purchased),
  )
  const convertedShares = sum(
    conversions.filter((c) => S_CONVERSION.has(norm(c.status)) && num(c.shares_requested) > 0),
    (c) => num(c.shares_requested),
  )
  const legacyShares = num(bal.legacy_shares)
  const transfersReceived = sum(tRecv.filter((t) => S_TRANSFER.has(norm(t.status))), (t) => num(t.shares_transferred))
  const transfersSent = sum(tSent.filter((t) => S_TRANSFER.has(norm(t.status))), (t) => num(t.shares_transferred))
  const netTransfers = transfersReceived - transfersSent
  const equityShares = activePurchaseShares + giftBonusShares + convertedShares + legacyShares + netTransfers

  // v4.8.250 DISPLAY ONLY — truthful paid/bonus split for the dashboard breakdown.
  // displayBonusShares folds the pure gift rows together with the bonus-as-shares rows so a
  // BOGO / Birthday / PCAP / Consolidated-Bonus share stays visible as a BONUS share instead
  // of masquerading as a purchased one. Clamped at 0 because a small number of historical
  // accounts carry more reversal than gift. displayPaidShares is what the member actually
  // bought: active purchase shares minus the bonus, commission and pool credits.
  // equityShares above is deliberately untouched — re-adding either figure would double-count.
  const displayBonusShares = Math.max(giftBonusShares + bonusAsShares, 0)
  const displayPaidShares = Math.max(
    activePurchaseShares - bonusAsShares - commissionLevelShares - performancePoolShares - acresRedemptionShares,
    0,
  )

  // ── Commission wallet ──
  const earnedUsdt =
    sum(cts.filter((c) => S_COMMISSION.has(norm(c.status))), (c) => num(c.usdt_commission)) +
    sum(mlcs.filter((c) => S_COMMISSION.has(norm(c.status))), (c) => num(c.usdt_commission))
  const earnedShares =
    sum(cts.filter((c) => S_COMMISSION.has(norm(c.status))), (c) => num(c.share_commission)) +
    sum(mlcs.filter((c) => S_COMMISSION.has(norm(c.status))), (c) => num(c.shares_purchased))
  const mlcEarnedUsdt = sum(mlcs.filter((c) => S_COMMISSION.has(norm(c.status))), (c) => num(c.usdt_commission))
  const mlcEarnedShares = sum(mlcs.filter((c) => S_COMMISSION.has(norm(c.status))), (c) => num(c.shares_purchased))
  const topupUsdt = sum(
    purchases.filter((p) => p.is_commission_topup && S_TOPUP.has(norm(p.status))),
    (p) => num(p.topup_payment_amount || p.total_amount),
  )
  const convertedUsdt = sum(conversions.filter((c) => S_CONVERSION.has(norm(c.status))), (c) => num(c.usdt_amount))
  const wExecTerminal = wExec.filter((w) => S_WITHDRAWAL.has(norm(w.status)))
  const withdrawnUsdt = wExecTerminal.length
    ? sum(wExecTerminal, (w) => num(w.net_amount || w.amount))
    : sum(wReq.filter((w) => S_WITHDRAWAL.has(norm(w.status))), (w) => num(w.withdrawal_amount))
  const reconstructableWalletUsdt = earnedUsdt + topupUsdt - convertedUsdt - withdrawnUsdt

  // Q2: anchor spendable USDT to the stored balance; preserve the unreconstructable gap.
  const walletUsdt = round(num(bal.usdt_balance), 2)
  const residual = round(walletUsdt - reconstructableWalletUsdt, 2)

  const flags = collectFlags({
    bal, earnedShares, earnedUsdt, mlcEarnedUsdt, mlcEarnedShares, conversions,
    walletUsdt, reconstructableWalletUsdt, residual, purchases, activePurchases, tRecv, convertedShares,
  })

  return {
    equityShares: round(equityShares, 8),
    wallet: { usdt: walletUsdt, shares: round(earnedShares, 8) },
    escrow: { usdt: round(num(bal.escrowed_amount), 2), shares: round(num(bal.escrowed_shares), 8) },
    residual,
    breakdown: {
      activePurchaseShares: round(activePurchaseShares, 8),
      giftBonusShares: round(giftBonusShares, 8),
      bonusAsShares: round(bonusAsShares, 8),
      displayBonusShares: round(displayBonusShares, 8),
      displayPaidShares: round(displayPaidShares, 8),
      commissionLevelShares: round(commissionLevelShares, 8),
      performancePoolShares: round(performancePoolShares, 8),
      acresRedemptionShares: round(acresRedemptionShares, 8),
      convertedShares: round(convertedShares, 8),
      legacyShares: round(legacyShares, 8),
      transfersReceived: round(transfersReceived, 8),
      transfersSent: round(transfersSent, 8),
      netTransfers: round(netTransfers, 8),
      earnedUsdt: round(earnedUsdt, 2),
      earnedShares: round(earnedShares, 8),
      topupUsdt: round(topupUsdt, 2),
      convertedUsdt: round(convertedUsdt, 2),
      withdrawnUsdt: round(withdrawnUsdt, 2),
      reconstructableWalletUsdt: round(reconstructableWalletUsdt, 2),
    },
    flags,
  }
}

/** Detect drift / anomalies / double-count risks. @returns {string[]} */
function collectFlags(x) {
  const flags = []
  const hasBal = x.bal && x.bal.usdt_balance !== undefined
  const dEarnedShares = num(x.bal.total_earned_shares) - x.earnedShares
  const dEarnedUsdt = num(x.bal.total_earned_usdt) - x.earnedUsdt
  if (hasBal && Math.abs(dEarnedShares) >= SHARE_TOL) flags.push('CACHE_SHARE_DRIFT')
  if (hasBal && Math.abs(dEarnedUsdt) >= USDT_TOL) flags.push('CACHE_USDT_DRIFT')
  if (x.mlcEarnedUsdt > USDT_TOL || x.mlcEarnedShares > SHARE_TOL) flags.push('MLC_IGNORED')
  if ((x.conversions || []).some((c) => S_CONVERSION.has(norm(c.status)) && num(c.usdt_amount) > 0 && num(c.shares_requested) === 0)) {
    flags.push('CONVERSION_ZERO_SHARES')
  }
  if (x.walletUsdt < -USDT_TOL || num(x.bal.share_balance) < -SHARE_TOL || x.reconstructableWalletUsdt < -USDT_TOL) {
    flags.push('NEGATIVE_WALLET')
  }
  if (Math.abs(x.residual) >= USDT_TOL) flags.push('MANUAL_ADJ_RESIDUAL')
  // Minor, non-systemic double-count risks (see plan Phase 0 sweep).
  const hasRecvPurchase = (x.activePurchases || []).some((p) => pm(p) === 'Received Transfers')
  const hasRecvRow = (x.tRecv || []).some((t) => S_TRANSFER.has(norm(t.status)))
  if (hasRecvPurchase && hasRecvRow) flags.push('RECEIVED_TRANSFER_DUP')
  const hasConvPurchase = (x.activePurchases || []).some((p) => pm(p).toLowerCase().includes('conversion'))
  if (hasConvPurchase && x.convertedShares > 0) flags.push('CONVERSION_MATERIALIZED_DUP')
  return flags
}
